import { educationLevelMap } from '@/components/patients/constants';
import { formatDateOnly } from '@/lib/date';
import { getAge } from '@/utils/getAge';
import { translateGender } from '@/utils/translateGender';
import type { EducationLevel } from '@/components/patients/types';
import type { Patient } from '@/types/patient';

export type PatientInfoField = { label: string; value: string };

/**
 * The patient's registered data as read-only label/value pairs, in a fixed order,
 * omitting missing fields. Shown in the anamnesis rail — never written into the
 * document (the psychologist is not editing the patient record here). Name is
 * excluded because it already appears in the page header.
 */
export function getPatientInfoFields(patient?: Patient): PatientInfoField[] {
    if (!patient) return [];

    const fields: PatientInfoField[] = [];

    if (patient.date_of_birth) {
        const age = Number(patient.age) || getAge(patient.date_of_birth);
        const formatted = formatDateOnly(patient.date_of_birth);
        fields.push({
            label: 'Data de nascimento',
            value: age > 0 ? `${formatted} (${age} anos)` : formatted,
        });
    }

    if (patient.gender) fields.push({ label: 'Sexo', value: translateGender(patient.gender) });
    if (patient.city) fields.push({ label: 'Cidade', value: patient.city });

    if (patient.education_level) {
        fields.push({
            label: 'Escolaridade',
            value:
                educationLevelMap[patient.education_level as EducationLevel] ??
                patient.education_level,
        });
    }

    const phone = patient.phone || patient.contact_number;
    if (phone) fields.push({ label: 'Telefone', value: phone });

    if (patient.referring_professional) {
        fields.push({ label: 'Encaminhado(a) por', value: patient.referring_professional });
    }

    return fields;
}
